import * as React from "react"

type AnimationCallback<T extends Element> = [
  React.AnimationEventHandler<T>,
  React.MutableRefObject<T | null>,
  AnimationPlayState | null,
]

/* Creates a ref and wraps the provided callback so that it is executed when a AnimationEvent fires
for the ref element. To use set the `ref` and `onAnimationEnd` on an element using the returned values.
 */
export function useAnimationCallback<T extends Element>(
  callback: (event: React.AnimationEvent<T>, ref: React.RefObject<T | null>) => void,
  animationNames?: string[]
): AnimationCallback<T> {
  const ref = React.useRef<T>(null)
  const wrappedCallback = React.useCallback(
    (event: React.AnimationEvent<T>) => {
      if (!animationNames || animationNames.includes(event.animationName)) {
        callback(event, ref)
      }
    },
    [ref, callback, animationNames]
  )

  const animationPlayState =
    ref.current && (window.getComputedStyle(ref.current)?.animationPlayState as AnimationPlayState)

  return [wrappedCallback, ref, animationPlayState]
}
